import React, { useEffect, useMemo, useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import { useProjects } from '../../contexts/ProjectsContext'
import { buildDefaultWpsNumber } from '../../utils/wpsNumberDefaults'
import Button from '../ui/Button'
import PageLoader from '../ui/PageLoader'

function readStoredWpsNumber(
  meta: { wpsNumber?: unknown } | null,
  wps: { wpsNumber?: unknown } | null,
  pipeSetWpsNumber?: string
): string {
  const fromMeta = String(meta?.wpsNumber ?? '').trim()
  if (fromMeta) return fromMeta
  const fromWps = String(wps?.wpsNumber ?? '').trim()
  if (fromWps) return fromWps
  return String(pipeSetWpsNumber ?? '').trim()
}

/**
 * Renders pipe-set child routes only after WPS sections have finished loading.
 * Prompts for WPS ID only for manual pipe sets that still have no stored ID.
 * After WPS PDF import Upload/Apply, meta/wps/pipeSet.wpsNumber are set and
 * this modal must not appear.
 */
export default function PipeSetSectionsOutlet() {
  const { id, pipeId } = useParams()
  const { getProject } = useProjects()
  const { loading, getSection, updateSection } = useWpsSections()
  const [showWpsPrompt, setShowWpsPrompt] = useState(false)
  const [wpsIdDraft, setWpsIdDraft] = useState('')
  const [savingWpsId, setSavingWpsId] = useState(false)

  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)

  const defaultWpsId = useMemo(() => {
    if (!id || !pipeId) return buildDefaultWpsNumber(undefined, undefined)
    return buildDefaultWpsNumber(project?.name, pipeId)
  }, [id, pipeId, project?.name])

  useEffect(() => {
    if (loading) return
    const metaSection = getSection('meta') as { wpsNumber?: unknown } | null
    const wpsSection = getSection('wps') as { wpsNumber?: unknown } | null
    const existingWpsId = readStoredWpsNumber(
      metaSection,
      wpsSection,
      pipeSet?.wpsNumber
    )
    if (existingWpsId) {
      setShowWpsPrompt(false)
      setWpsIdDraft(existingWpsId)
      return
    }
    // Manual create only — empty after import Upload/Apply should never reach here.
    setWpsIdDraft(defaultWpsId)
    setShowWpsPrompt(true)
  }, [defaultWpsId, getSection, loading, pipeSet?.wpsNumber])

  const saveWpsId = async () => {
    if (savingWpsId) return
    const normalized = wpsIdDraft.trim() || defaultWpsId
    const metaSection = (getSection('meta') as Record<string, unknown> | null) ?? {}
    const wpsSection = (getSection('wps') as Record<string, unknown> | null) ?? {}
    setSavingWpsId(true)
    try {
      await updateSection('meta', {
        ...metaSection,
        wpsNumber: normalized
      })
      await updateSection('wps', {
        ...wpsSection,
        wpsNumber: normalized
      })
      setWpsIdDraft(normalized)
      setShowWpsPrompt(false)
    } finally {
      setSavingWpsId(false)
    }
  }

  if (loading) {
    return <PageLoader label="Loading WPS data…" />
  }
  return (
    <>
      <Outlet />
      {showWpsPrompt ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-base font-semibold text-gray-900">Set WPS ID</h2>
            <p className="mt-1 text-sm text-gray-500">
              Enter the WPS ID for this pipe set.
            </p>
            <label className="mt-4 block text-xs font-medium text-gray-500">WPS ID</label>
            <input
              className="input-primary mt-1"
              value={wpsIdDraft}
              onChange={(event) => setWpsIdDraft(event.target.value)}
              placeholder="WPS ID"
              autoFocus
            />
            <div className="mt-5 flex justify-end">
              <Button type="button" onClick={() => void saveWpsId()} disabled={savingWpsId}>
                {savingWpsId ? 'Saving…' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
